import {
  ApiReport,
  PublicReportDto,
  SubmitFeedbackRequest,
  SubmitFeedbackResponse,
  AccessTokenRequest,
  AccessTokenResponse,
  AccessLinkBrandResponse,
} from '../types/models.types';
import { ApiResponse } from '../types/api.types';
import { apiService } from './api.service';
import {
  OrderTimelineDto,
  PagedModelTimelineItemDto,
} from '../generatedClient';
import { mapOrderStatusRA2ToReport } from '../mappers/order-status.mapper';
import { mapPublicReportToUI } from '../mappers/public-report.mapper';
import type { MappedPublicReport } from '../mappers/public-report.mapper';
import type { ApiResponseRaportV2 } from '../mappers/hsp/api';
import type { components } from '../types/generated';

export type PublicGalleryVariantRequest =
  components['schemas']['GalleryPresignedUrlsRequest']['items'][number];
export type PublicGalleryVariantBatchResponse =
  components['schemas']['VariantUrlBatchDto'];

export const PUBLIC_GALLERY_VARIANTS_BATCH_LIMIT = 200;

export interface ResendPinRequest {
  email: string;
}

export class ReportService {
  private timelineEndpoint = '/orders/timeline';
  private publicReportsEndpoint = '/public/reports';
  private publicOrdersEndpoint = '/public/orders';

  /**
   * @deprecated Użyj getPublicOrderStatus() - nowy endpoint zwraca więcej informacji
   */
  async getOrderStatusRA2AsReport(
    orderUuid: string,
  ): Promise<ApiResponse<ApiReport>> {
    const response = await apiService.get<OrderTimelineDto>(
      `${this.timelineEndpoint}/${orderUuid}`,
      undefined,
    );

    const mappedData = mapOrderStatusRA2ToReport(response.data);

    return {
      data: mappedData,
      message: 'Pobrano status zamówienia',
      status: response.status,
    };
  }

  async getPublicOrderStatus(
    orderUuid: string,
  ): Promise<ApiResponse<OrderTimelineDto>> {
    const response = await apiService.get<OrderTimelineDto>(
      `${this.publicOrdersEndpoint}/${orderUuid}/status`,
      undefined,
    );

    return {
      data: response.data,
      message: 'Pobrano status zamówienia',
      status: response.status,
    };
  }

  async getOrderTimeline(
    orderId: string,
    page: number = 0,
    size: number = 20,
    sort?: string[],
  ): Promise<ApiResponse<PagedModelTimelineItemDto>> {
    const params = new URLSearchParams({
      orderId,
      page: page.toString(),
      size: size.toString(),
    });
    sort?.forEach((s) => params.append('sort', s));

    const response = await apiService.get<PagedModelTimelineItemDto>(
      `${this.timelineEndpoint}?${params.toString()}`,
      undefined,
    );

    return {
      data: response.data,
      message: 'Pobrano timeline zamówienia',
      status: response.status,
    };
  }

  async getVehicleTimeline(
    vehicleId: string,
    page: number = 0,
    size: number = 20,
    sort?: string[],
  ): Promise<ApiResponse<PagedModelTimelineItemDto>> {
    const params = new URLSearchParams({
      vehicleId,
      page: page.toString(),
      size: size.toString(),
    });
    sort?.forEach((s) => params.append('sort', s));

    const response = await apiService.get<PagedModelTimelineItemDto>(
      `${this.timelineEndpoint}?${params.toString()}`,
      undefined,
    );

    return {
      data: response.data,
      message: 'Pobrano timeline pojazdu',
      status: response.status,
    };
  }

  async getVehicleInOrderTimeline(
    orderId: string,
    vehicleId: string,
    page: number = 0,
    size: number = 20,
    sort?: string[],
  ): Promise<ApiResponse<PagedModelTimelineItemDto>> {
    const params = new URLSearchParams({
      orderId,
      vehicleId,
      page: page.toString(),
      size: size.toString(),
    });
    sort?.forEach((s) => params.append('sort', s));

    const response = await apiService.get<PagedModelTimelineItemDto>(
      `${this.timelineEndpoint}?${params.toString()}`,
      undefined,
    );

    return {
      data: response.data,
      message: 'Pobrano timeline pojazdu w zamówieniu',
      status: response.status,
    };
  }

  /**
   * Klient: marka (brand) linku dostępowego — po samym `linkId`, bez email/PIN.
   * Służy do brandingu ekranu logowania (PIN): front renderuje właściwe logo,
   * zanim klient poda dane. Backend zwraca markę także dla linków
   * wygasłych/wyłączonych.
   */
  async getAccessLinkBrand(
    linkId: string,
  ): Promise<ApiResponse<AccessLinkBrandResponse>> {
    const response = await apiService.get<AccessLinkBrandResponse>(
      `${this.publicReportsEndpoint}/access-links/${linkId}/brand`,
      undefined,
    );

    return {
      data: response.data,
      message: 'Pobrano markę linku dostępowego',
      status: response.status,
    };
  }

  /**
   * Klient: weryfikacja linku dostępowego email + PIN.
   * Endpoint nie wymaga reportId — backend rozwiązuje raport po `linkId` z query.
   * Zwraca token JWT (5h) + pełny raport. Po sukcesie token jest zapisywany w apiService.
   */
  async verifyAccessLink(
    linkId: string,
    email: string,
    pin: string,
  ): Promise<ApiResponse<AccessTokenResponse>> {
    const response = await apiService.post<
      AccessTokenResponse,
      AccessTokenRequest
    >(
      `${this.publicReportsEndpoint}/verify`,
      { email, pin },
      { linkId },
    );

    if (response.data?.token) {
      apiService.setAuthToken(response.data.token);
    }

    return response;
  }

  /**
   * Klient: pobranie raportu po zalogowaniu — raport rozwiązywany z tokenu JWT.
   *
   * Jeśli podany `token` (np. quick-access z URL `?token=...`), idzie ad-hoc
   * w nagłówku Authorization tylko dla tego requestu — nie nadpisuje sesji
   * klienta zapisanej w sessionStorage.
   */
  async getPublicReport(
    token?: string,
  ): Promise<ApiResponse<MappedPublicReport>> {
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    const response = await apiService.get<PublicReportDto>(
      this.publicReportsEndpoint,
      undefined,
      headers,
    );

    const mappedData = mapPublicReportToUI(
      response.data as PublicReportDto & { hspSection: ApiResponseRaportV2 },
    );

    return {
      data: mappedData,
      message: 'Pobrano raport publiczny',
      status: response.status,
    };
  }

  /**
   * Klient: pobranie presigned URL-i wariantów galerii.
   * Raport i brand są wyliczane po tokenie; frontend nie przekazuje ich w body.
   */
  async getPublicGalleryVariantUrls(
    items: PublicGalleryVariantRequest[],
    token?: string,
  ): Promise<ApiResponse<PublicGalleryVariantBatchResponse>> {
    if (
      items.length === 0 ||
      items.length > PUBLIC_GALLERY_VARIANTS_BATCH_LIMIT
    ) {
      throw new Error(
        `Batch wariantów galerii musi zawierać od 1 do ${PUBLIC_GALLERY_VARIANTS_BATCH_LIMIT} zdjęć.`,
      );
    }

    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    return apiService.post<
      PublicGalleryVariantBatchResponse,
      components['schemas']['GalleryPresignedUrlsRequest']
    >(
      `${this.publicReportsEndpoint}/gallery/images/presigned-urls`,
      { items },
      undefined,
      headers,
    );
  }

  /**
   * Klient: wymuszenie wygenerowania nowego PIN-u (stary natychmiast traci ważność).
   * Wymaga emaila zgodnego z linkiem — w przeciwnym wypadku 401.
   */
  async resendPin(linkId: string, email: string): Promise<ApiResponse<null>> {
    return apiService.post<null, ResendPinRequest>(
      `${this.publicReportsEndpoint}/resend-pin`,
      { email },
      { linkId },
    );
  }

  async submitFeedback(
    data: SubmitFeedbackRequest,
  ): Promise<ApiResponse<SubmitFeedbackResponse>> {
    return apiService.post<SubmitFeedbackResponse, SubmitFeedbackRequest>(
      `${this.publicReportsEndpoint}/feedback`,
      data,
    );
  }

  /**
   * Klient: pobranie ZIP-a z całą galerią. Raport rozwiązywany z tokenu JWT.
   *
   * Jeśli podany `token` (np. quick-access z URL `?token=...`), idzie ad-hoc
   * w nagłówku Authorization — analogicznie do `getPublicReport` — żeby
   * sesja klienta zapisana w sessionStorage nie była nadpisywana.
   */
  async downloadGalleryZip(
    filenameHint?: string,
    token?: string,
  ): Promise<void> {
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const { blob, filename } = await apiService.getBlob(
      `${this.publicReportsEndpoint}/gallery/download-zip`,
      undefined,
      headers,
    );

    triggerBrowserDownload(blob, filename ?? filenameHint ?? 'gallery.zip');
  }
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const reportService = new ReportService();
